import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import FormInputText from '../../common/Forms/FormTextField';
import InputDropdown from '../../common/Forms/InputDropdown';
import FormInputMultiCheckbox from '../../common/Forms/FormCheckbox';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { Auth } from 'aws-amplify';
import {
  setIsProfB2DataLoaded,
  setAlcohol,
  setMethamphetamine,
  setCocaine,
  setHeroin,
  setMarijuanaHash,
  setEcstacy,
  setPCP,
  setLSDHallucinogens,
  setInhalants,
  setPrescription,
  setPolyDrugUser,
  setSubstanceUseHistoryOther,
  setSubstanceUseHistoryComment,
  setPA2ConcernsNone,
  setSex,
  setArson,
  setViolence,
  setChildAbuse,
  setDomesticViolence,
  setPOC,
  setWeapons,
  setGangs,
  setEnemies,
  setADA,
  setMedical,
  setPsychiatric,
  setVictims,
  setElderAbuse,
  setPA2ConcernsOthers,
  setPA2DirectionsNone,
  setNoCoEdProgram,
  setNoContactwithMinors,
  setNoFOTEP,
  setMedicalConcerns,
  setPA2DirectionsComments,
  setTIP,
  setFOTEP,
  setEligibleTo2933,
  setInEligibleTo2933,
  setOSAPAgentsGeneralComments,
  setDateScreened,
  setOSAPParoleAgentId,
  getProfB2Form,
} from '../../../redux/episode/profB2Tab/profB2FormSlice';
import {
  EPISODE_PROFB2_UPDATE_URL,
  EPISODE_PROFB2_URL,
  formDateFormat,
  yesNoDropdown,
  maxLength
} from '../../../constants';
import { setStopApi } from '../../../services/StopApi/StopApi';
import FormSuccess from '../../common/Forms/FormSuccess';
import ProgressBar from '../../common/ProgressBar/ProgressBar';
import CancelPopUp from '../../common/CancelPopUp/CancelPopUp';
import { setRefreshEpisodeParticipant } from '../../../redux/episode/episodeParticipant/episodeParticipantSlice';
import {
  getSaveCancel,
  useLoading,
} from '../../common/commonUtils/commonUtils';
import { PrintViewGrid } from './ProfA1PdfFormCommonUtils';
import ProfB1PdfForm from './ProfB1PDF/ProfB1PdfForm';

const schema = yup.object().shape({
  OSAPAgentsGeneralComments: yup.string().nullable().max(800, `${maxLength} 800`),
});

const ProfB2 = (props) => {
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [cancelPopUp, setCancelPopUp] = useState(false);
  const [openProfilePDF, setOpenProfilePDF] = useState(false);
  const { loading, hideLoading } = useLoading();

  const episodeId = useSelector(
    (state) => state?.episodeDetail?.singleEpisode[0]?.episodeId
  );
  const profB2Form = useSelector((state) => state?.profB2Form);
  const profB2RefernceData = useSelector((state) => state?.profB2RefernceData);

  const defaultValue = {
    participantName: profB2Form?.participant?.participantName,
    CDCNumber: profB2Form?.CDCNumber,
    alcohol: profB2Form?.alcohol,
    methamphetamine: profB2Form?.methamphetamine,
    cocaine: profB2Form?.cocaine,
    heroin: profB2Form?.heroin,
    marijuanaHash: profB2Form?.marijuanaHash,
    ecstacy: profB2Form?.ecstacy,
    PCP: profB2Form?.PCP,
    LSDHallucinogens: profB2Form?.LSDHallucinogens,
    inhalants: profB2Form?.inhalants,
    prescription: profB2Form?.prescription,
    polyDrugUser: profB2Form?.polyDrugUser,
    substanceUseHistoryOther: profB2Form?.substanceUseHistoryOther,
    substanceUseHistoryComment: profB2Form?.substanceUseHistoryComment,
    PA2ConcernsNone: profB2Form?.PA2ConcernsNone,
    sex: profB2Form?.sex,
    arson: profB2Form?.arson,
    violence: profB2Form?.violence,
    childAbuse: profB2Form?.childAbuse,
    domesticViolence: profB2Form?.domesticViolence,
    POC: profB2Form?.POC,
    weapons: profB2Form?.weapons,
    gangs: profB2Form?.gangs,
    enemies: profB2Form?.enemies,
    ADA: profB2Form?.ADA,
    medical: profB2Form?.medical,
    psychiatric: profB2Form?.psychiatric,
    victims: profB2Form?.victims,
    elderAbuse: profB2Form?.elderAbuse,
    PA2ConcernsOthers: profB2Form?.PA2ConcernsOthers,
    PA2DirectionsNone: profB2Form?.PA2DirectionsNone,
    noCoEdProgram: profB2Form?.noCoEdProgram,
    noContactwithMinors: profB2Form?.noContactwithMinors,
    noFOTEP: profB2Form?.noFOTEP,
    medicalConcerns: profB2Form?.medicalConcerns,
    PA2DirectionsComments: profB2Form?.PA2DirectionsComments,
    TIP: profB2Form?.TIP === null ? '' : profB2Form?.TIP,
    FOTEP: profB2Form?.FOTEP === null ? '' : profB2Form?.FOTEP,
    eligibleTo2933:
      profB2Form?.eligibleTo2933 === null ? '' : profB2Form?.eligibleTo2933,
    inEligibleTo2933:
      profB2Form?.inEligibleTo2933 === null ? '' : profB2Form?.inEligibleTo2933,
    OSAPAgentsGeneralComments: profB2Form?.OSAPAgentsGeneralComments,
    dateScreened: moment(profB2Form?.dateScreened).format(formDateFormat),
    OSAPParoleAgentId: profB2Form?.OSAPParoleAgentId
      ? profB2Form?.OSAPParoleAgentId
      : '',
  };

  const useFunction = useForm({ defaultValues: defaultValue, resolver: yupResolver(schema) });
  const { handleSubmit, reset, control, formState, setValue, watch, register } = useFunction;
  const { errors } = formState;

  useEffect(() => {
    if (user?.signInUserSession.accessToken.jwtToken) {
      fetchData();
    }
    getUser().then((userData) => setUser(userData));
  }, [user?.signInUserSession.accessToken.jwtToken]);

  const fetchData = async () => {
    let profB2TabUrl = `${EPISODE_PROFB2_URL}/${episodeId}/ProfB2`;
    const authorization = `${user?.signInUserSession.accessToken.jwtToken}`;
    if (profB2Form?.isProfB2DataLoaded === false) {
      dispatch(getProfB2Form({ profB2TabUrl, authorization }));
      setTimeout(() => {
        dispatch(setIsProfB2DataLoaded(true));
      }, 1000);
    }
    setTimeout(() => {
      hideLoading();
    }, 500);
  };

  const getUser = () => {
    return Auth.currentAuthenticatedUser()
      .then((userData) => userData)
      .catch(() => console.log('Not signed in'));
  };

  useEffect(() => {
    reset(defaultValue);
  }, [
    defaultValue.participantName,
    defaultValue.CDCNumber,
    defaultValue.alcohol,
    defaultValue.methamphetamine,
    defaultValue.cocaine,
    defaultValue.heroin,
    defaultValue.marijuanaHash,
    defaultValue.ecstacy,
    defaultValue.PCP,
    defaultValue.LSDHallucinogens,
    defaultValue.inhalants,
    defaultValue.prescription,
    defaultValue.polyDrugUser,
    defaultValue.substanceUseHistoryOther,
    defaultValue.substanceUseHistoryComment,
    defaultValue.PA2ConcernsNone,
    defaultValue.sex,
    defaultValue.arson,
    defaultValue.violence,
    defaultValue.childAbuse,
    defaultValue.domesticViolence,
    defaultValue.POC,
    defaultValue.weapons,
    defaultValue.gangs,
    defaultValue.enemies,
    defaultValue.ADA,
    defaultValue.medical,
    defaultValue.psychiatric,
    defaultValue.victims,
    defaultValue.elderAbuse,
    defaultValue.PA2ConcernsOthers,
    defaultValue.PA2DirectionsNone,
    defaultValue.noCoEdProgram,
    defaultValue.noContactwithMinors,
    defaultValue.noFOTEP,
    defaultValue.medicalConcerns,
    defaultValue.PA2DirectionsComments,
    defaultValue.TIP,
    defaultValue.FOTEP,
    defaultValue.eligibleTo2933,
    defaultValue.inEligibleTo2933,
    defaultValue.CDD,
    defaultValue.CDD_MAX,
    defaultValue.OSAPAgentsGeneralComments,
    defaultValue.dateScreened,
    defaultValue.OSAPParoleAgentId,
  ]);

  const onSubmit = () => {
    const url = EPISODE_PROFB2_UPDATE_URL;
    const payload = {
      episodeID: episodeId,
      alcohol: profB2Form?.alcohol,
      methamphetamine: profB2Form?.methamphetamine,
      cocaine: profB2Form?.cocaine,
      heroin: profB2Form?.heroin,
      marijuanaHash: profB2Form?.marijuanaHash,
      ecstacy: profB2Form?.ecstacy,
      PCP: profB2Form?.PCP,
      LSDHallucinogens: profB2Form?.LSDHallucinogens,
      inhalants: profB2Form?.inhalants,
      prescription: profB2Form?.prescription,
      polyDrugUser: profB2Form?.polyDrugUser,
      substanceUseHistoryOther: profB2Form?.substanceUseHistoryOther,
      substanceUseHistoryComment: profB2Form?.substanceUseHistoryComment,
      PA2ConcernsNone: profB2Form?.PA2ConcernsNone,
      sex: profB2Form?.sex,
      arson: profB2Form?.arson,
      violence: profB2Form?.violence,
      childAbuse: profB2Form?.childAbuse,
      domesticViolence: profB2Form?.domesticViolence,
      POC: profB2Form?.POC,
      weapons: profB2Form?.weapons,
      gangs: profB2Form?.gangs,
      enemies: profB2Form?.enemies,
      ADA: profB2Form?.ADA,
      medical: profB2Form?.medical,
      psychiatric: profB2Form?.psychiatric,
      victims: profB2Form?.victims,
      elderAbuse: profB2Form?.elderAbuse,
      PA2ConcernsOthers: profB2Form?.PA2ConcernsOthers,
      PA2DirectionsNone: profB2Form?.PA2DirectionsNone,
      noCoEdProgram: profB2Form?.noCoEdProgram,
      noContactwithMinors: profB2Form?.noContactwithMinors,
      noFOTEP: profB2Form?.noFOTEP,
      medicalConcerns: profB2Form?.medicalConcerns,
      PA2DirectionsComments: profB2Form?.PA2DirectionsComments,
      TIP: profB2Form?.TIP === '' ? null : profB2Form?.TIP,
      FOTEP: profB2Form?.FOTEP === '' ? null : profB2Form?.FOTEP,
      eligibleTo2933:
        profB2Form?.eligibleTo2933 === '' ? null : profB2Form?.eligibleTo2933,
      inEligibleTo2933:
        profB2Form?.inEligibleTo2933 === ''
          ? null
          : profB2Form?.inEligibleTo2933,
      OSAPAgentsGeneralComments: profB2Form?.OSAPAgentsGeneralComments,
      dateScreened: moment(new Date()).format(formDateFormat),
      OSAPParoleAgentId: profB2Form?.OSAPParoleAgentId
        ? profB2Form?.OSAPParoleAgentId
        : null,
    };
    const authorization = `${user?.signInUserSession.accessToken.jwtToken}`;
    setStopApi({ url, payload, authorization }).then((response) => {
      if (response.succeeded === true) {
        setFormSuccess(true);
        dispatch(setRefreshEpisodeParticipant(true));
        setTimeout(() => {
          dispatch(setRefreshEpisodeParticipant(false));
        }, 1000);
      }
    }).catch((e) => alert(e));
  };

  const onError = () => {
    alert('Unable to Save. Please check for field validation errors.')
  }  

  const handleCancelPopUp = () => {
    setCancelPopUp(true);
  };

  const handleOpenB1Pdf = () => {
    setOpenProfilePDF(true);
  };

  if (loading) {
    return <ProgressBar />;
  }

  return (
    <>
      <PrintViewGrid handleOpenPdf={handleOpenB1Pdf} />
      <Grid container spacing={2} style={{ marginTop: '10px' }}>
        <Grid item xs={12} sm={4}>
          <FormInputText
            name="CDCNumber"
            type="text"
            control={control}
            disabled={true}
            label="CDC Number"
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          <FormInputText
            name="participantName"
            type="text"
            control={control}
            disabled={true}
            label="Participant Name"
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5">
            {' '}
            Substance Use History (Check all applicable)
          </Typography>
          <FormInputMultiCheckbox
            control={control}
            name="alcohol"
            label="Alcohol"
            checked={defaultValue?.alcohol}
            register={register('alcohol', {
              onBlur: (e) => dispatch(setAlcohol(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="methamphetamine"
            label="Methamphetamine"
            checked={defaultValue?.methamphetamine}
            register={register('methamphetamine', {
              onBlur: (e) => dispatch(setMethamphetamine(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="cocaine"
            label="Cocaine"
            checked={defaultValue?.cocaine}
            register={register('cocaine', {
              onBlur: (e) => dispatch(setCocaine(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="heroin"
            label="Heroin"
            checked={defaultValue?.heroin}
            register={register('heroin', {
              onBlur: (e) => dispatch(setHeroin(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="marijuanaHash"
            label="Marijuana/Hash"
            checked={defaultValue?.marijuanaHash}
            register={register('marijuanaHash', {
              onBlur: (e) => dispatch(setMarijuanaHash(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="ecstacy"
            label="Ecstacy"
            checked={defaultValue?.ecstacy}
            register={register('ecstacy', {
              onBlur: (e) => dispatch(setEcstacy(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="PCP"
            label="PCP"
            checked={defaultValue?.PCP}
            register={register('PCP', {
              onBlur: (e) => dispatch(setPCP(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="LSDHallucinogens"
            label="LSD/Hallucinogens"
            checked={defaultValue?.LSDHallucinogens}
            register={register('LSDHallucinogens', {
              onBlur: (e) => dispatch(setLSDHallucinogens(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="inhalants"
            label="Inhalants"
            checked={defaultValue?.inhalants}
            register={register('inhalants', {
              onBlur: (e) => dispatch(setInhalants(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="prescription"
            label="Prescription"
            checked={defaultValue?.prescription}
            register={register('prescription', {
              onBlur: (e) => dispatch(setPrescription(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="polyDrugUser"
            label="Poly-Drug User"
            checked={defaultValue?.polyDrugUser}
            register={register('polyDrugUser', {
              onBlur: (e) => dispatch(setPolyDrugUser(e.target.value)),
            })}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormInputText
                name="substanceUseHistoryOther"
                type="text"
                control={control}
                label="Other"
                register={register('substanceUseHistoryOther', {
                  onBlur: (e) =>
                    dispatch(setSubstanceUseHistoryOther(e.target.value)),
                })}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <FormInputText
                name="substanceUseHistoryComment"
                type="text"
                control={control}
                label="Comments"
                register={register('substanceUseHistoryComment', {
                  onBlur: (e) =>
                    dispatch(setSubstanceUseHistoryComment(e.target.value)),
                })}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h5">Participant All Concerns</Typography>
          <Typography variant="h6">
            (If YES, check all applicable. See General Comments for specific
            information)
          </Typography>
          <FormInputMultiCheckbox
            control={control}
            name="PA2ConcernsNone"
            label="None"
            checked={defaultValue?.PA2ConcernsNone}
            register={register('PA2ConcernsNone', {
              onBlur: (e) => dispatch(setPA2ConcernsNone(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="sex"
            label="Sex"
            checked={defaultValue?.sex}
            register={register('sex', {
              onBlur: (e) => dispatch(setSex(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="arson"
            label="Arson"
            checked={defaultValue?.arson}
            register={register('arson', {
              onBlur: (e) => dispatch(setArson(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="violence"
            label="Violence"
            checked={defaultValue?.violence}
            register={register('violence', {
              onBlur: (e) => dispatch(setViolence(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="childAbuse"
            label="Child Abuse"
            checked={defaultValue?.childAbuse}
            register={register('childAbuse', {
              onBlur: (e) => dispatch(setChildAbuse(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="domesticViolence"
            label="Domestic Violence"
            checked={defaultValue?.domesticViolence}
            register={register('domesticViolence', {
              onBlur: (e) => dispatch(setDomesticViolence(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="POC"
            label="POC"
            checked={defaultValue?.POC}
            register={register('POC', {
              onBlur: (e) => dispatch(setPOC(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="weapons"
            label="Weapons"
            checked={defaultValue?.weapons}
            register={register('weapons', {
              onBlur: (e) => dispatch(setWeapons(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="gangs"
            label="Gangs"
            checked={defaultValue?.gangs}
            register={register('gangs', {
              onBlur: (e) => dispatch(setGangs(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="enemies"
            label="Enemies"
            checked={defaultValue?.enemies}
            register={register('enemies', {
              onBlur: (e) => dispatch(setEnemies(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="ADA"
            label="ADA"
            checked={defaultValue?.ADA}
            register={register('ADA', {
              onBlur: (e) => dispatch(setADA(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="medical"
            label="Medical"
            checked={defaultValue?.medical}
            register={register('medical', {
              onBlur: (e) => dispatch(setMedical(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="psychiatric"
            label="Psychiatric"
            checked={defaultValue?.psychiatric}
            register={register('psychiatric', {
              onBlur: (e) => dispatch(setPsychiatric(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="victims"
            label="Victims"
            checked={defaultValue?.victims}
            register={register('victims', {
              onBlur: (e) => dispatch(setVictims(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="elderAbuse"
            label="ElderAbuse"
            checked={defaultValue?.elderAbuse}
            register={register('elderAbuse', {
              onBlur: (e) => dispatch(setElderAbuse(e.target.value)),
            })}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormInputText
                name="PA2ConcernsOthers"
                type="text"
                control={control}
                label="Others"
                register={register('PA2ConcernsOthers', {
                  onBlur: (e) =>
                    dispatch(setPA2ConcernsOthers(e.target.value)),
                })}
              />
            </Grid>
            <Grid item xs={12} sm={8}></Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h5">
            Participant All Directions For Placement
          </Typography>
          <FormInputMultiCheckbox
            control={control}
            name="PA2DirectionsNone"
            label="None"
            checked={defaultValue?.PA2DirectionsNone}
            register={register('PA2DirectionsNone', {
              onBlur: (e) => dispatch(setPA2DirectionsNone(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="noCoEdProgram"
            label="No Co-Ed Program"
            checked={defaultValue?.noCoEdProgram}
            register={register('noCoEdProgram', {
              onBlur: (e) => dispatch(setNoCoEdProgram(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="noContactwithMinors"
            label="No Contact with Minors"
            checked={defaultValue?.noContactwithMinors}
            register={register('noContactwithMinors', {
              onBlur: (e) => dispatch(setNoContactwithMinors(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="noFOTEP"
            label="No FOTEP"
            checked={defaultValue?.noFOTEP}
            register={register('noFOTEP', {
              onBlur: (e) => dispatch(setNoFOTEP(e.target.value)),
            })}
          />
          <FormInputMultiCheckbox
            control={control}
            name="medicalConcerns"
            label="Medical Concerns (Explain)"
            checked={defaultValue?.medicalConcerns}
            register={register('medicalConcerns', {
              onBlur: (e) => dispatch(setMedicalConcerns(e.target.value)),
            })}
          />
          <FormInputText
            name="PA2DirectionsComments"
            type="text"
            control={control}
            label="Comments"
            register={register('PA2DirectionsComments', {
              onBlur: (e) =>
                dispatch(setPA2DirectionsComments(e.target.value)),
            })}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={3}>
          <InputDropdown
            name="TIP"
            control={control}
            options={yesNoDropdown}
            defaultId={defaultValue?.TIP}
            register={register('TIP', {
              onBlur: (e) => dispatch(setTIP(e.target.value)),
            })}
            label="TIP"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <InputDropdown
            name="FOTEP"
            control={control}
            options={yesNoDropdown}
            defaultId={defaultValue?.FOTEP}
            register={register('FOTEP', {
              onBlur: (e) => dispatch(setFOTEP(e.target.value)),
            })}
            label="FOTEP"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <InputDropdown
            name="eligibleTo2933"
            control={control}
            options={yesNoDropdown}
            defaultId={defaultValue?.eligibleTo2933}
            register={register('eligibleTo2933', {
              onBlur: (e) => dispatch(setEligibleTo2933(e.target.value)),
            })}
            label="2933 Eligible"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <InputDropdown
            name="inEligibleTo2933"
            control={control}
            options={yesNoDropdown}
            defaultId={defaultValue?.inEligibleTo2933}
            register={register('inEligibleTo2933', {
              onBlur: (e) => dispatch(setInEligibleTo2933(e.target.value)),
            })}
            label="2933 In Eligible"
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormInputText
            name="OSAPAgentsGeneralComments"
            type="text"
            control={control}
            multiline={true}
            rows={3.5}
            label="OSAP Agents General Comments"
            register={register('OSAPAgentsGeneralComments', {
              onBlur: (e) =>
                dispatch(setOSAPAgentsGeneralComments(e.target.value)),
            })}
            errors={!!errors.OSAPAgentsGeneralComments}
            helperText={errors?.OSAPAgentsGeneralComments?.message}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <InputDropdown
            name="OSAPParoleAgentId"
            control={control}
            options={profB2RefernceData?.osapParoleAgents}
            defaultId={defaultValue?.OSAPParoleAgentId}
            valueAsKey={true}
            register={register('OSAPParoleAgentId', {
              onBlur: (e) => dispatch(setOSAPParoleAgentId(e.target.value)),
            })}
            label="OSAP Parole Agent Name"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormInputText
            name="signature"
            type="text"
            control={control}
            disabled={true}
            label="Signature"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormInputText
            name="dateScreened"
            type="date"
            control={control}
            shrink={true}
            label="Date Screened"
            register={register('dateScreened', {
              onBlur: (e) => dispatch(setDateScreened(e.target.value)),
            })}
          />
        </Grid>
      </Grid>
      {getSaveCancel(handleCancelPopUp, null, handleSubmit(onSubmit, onError))}
      <FormSuccess formSuccess={formSuccess} setFormSuccess={setFormSuccess} />
      <CancelPopUp cancelPopUp={cancelPopUp} setCancelPopUp={setCancelPopUp} />
      <ProfB1PdfForm open={openProfilePDF} setOpen={setOpenProfilePDF} />
    </>
  );
};

export default ProfB2;
